import { Box, Button, H5, Text } from "@adfenix/alexandria";
import { Container, Row } from "react-grid-system";
import { BooleanCell } from "components/Datatable/BooleanCell";
import { Table } from "components/Datatable";
import Popover from "components/Popover";
import UilEditAlt from "@iconscout/react-unicons/icons/uil-edit-alt";
import UilTrashAlt from "@iconscout/react-unicons/icons/uil-trash-alt";
import { useFieldArray } from "react-hook-form";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import { useCheckPermission } from "hooks/usePermissions";
import { ProgramService } from "@adfenix/types";
import { useDisabledFields } from "../useDisabledFields";
import { useTranslation } from "react-i18next";

export const BundleGroupOverviewItem = ({
  bundleGroup,
  bundleGroupIndex,
  onAction,
  editBundleGroup,
  createSnapshot,
  setIsNewBundle
}: {
  bundleGroup: ProgramService.BundleGroupModel;
  bundleGroupIndex: number;
  onAction: (bundleIndex: number) => void;
  editBundleGroup: (index: number) => void;
  createSnapshot: () => void;
  setIsNewBundle: (isNew: boolean) => void;
}) => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { remove, append, fields } = useFieldArray({
    name: `bundleGroups.${bundleGroupIndex}.bundles`
  });

  const { remove: removeBundleGroup } = useFieldArray({
    name: `bundleGroups`
  });

  const canSeeBudget = useCheckPermission({
    scope: "Budget",
    action: "View"
  });

  const { hq } = useOfficeAndHq();

  return (
    <>
      <Box>
        <Container>
          <Row style={{ justifyContent: "space-between" }}>
            <Box display="flex" alignItems="center">
              <H5>{bundleGroup.name}</H5>
              <BooleanCell checked={bundleGroup.default ?? false} />{" "}
              {bundleGroup.default && (
                <span>
                  {t(
                    "programBuilder.program.details.bundleGroup.overview.active"
                  )}
                </span>
              )}
              <BooleanCell checked={bundleGroup.configurable ?? false} />{" "}
              {bundleGroup.configurable && (
                <span>
                  {t(
                    "programBuilder.program.details.bundleGroup.overview.configurable"
                  )}
                </span>
              )}
            </Box>

            <Box>
              <UilEditAlt
                onClick={() => {
                  editBundleGroup(bundleGroupIndex);
                }}
                style={{ cursor: "pointer" }}
                role="button"
                data-testid={`edit-bundle-group-${bundleGroupIndex}`}
              />
              {!disabledFields("bundleGroups") && (
                <UilTrashAlt
                  onClick={() => {
                    const confirm = window.confirm(
                      t(
                        "programBuilder.program.details.bundleGroup.delete.confirmationMessage"
                      )
                    );

                    if (confirm) {
                      removeBundleGroup(bundleGroupIndex);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                  role="button"
                />
              )}
            </Box>
          </Row>
        </Container>

        <Table
          pagination={false}
          style={{ zIndex: 1 }}
          columns={[
            {
              name: t("programBuilder.program.details.bundle.table.name.label"),
              selector: "name",
              cell: (row: ProgramService.BundleModel) => (
                <Text bold data-tag="allowRowEvents">
                  {row.name}
                </Text>
              )
            },
            {
              name: t("programBuilder.program.details.bundle.table.type.label"),
              selector: "type",
              cell: () => (
                <Text bold data-tag="allowRowEvents">
                  {bundleGroup.legacyType}
                </Text>
              )
            },
            {
              name: t(
                "programBuilder.program.details.bundle.table.duration.label"
              ),
              selector: "duration",
              grow: 0.2,
              cell: row => (
                <Text bold data-tag="allowRowEvents">
                  {row.metaTemplates?.[0]?.durationDays ??
                    row.deltaTemplates?.[0]?.durationDays ??
                    row.customTemplates?.[0]?.durationDays ??
                    0}{" "}
                  days
                </Text>
              )
            },
            {
              name: t(
                "programBuilder.program.details.bundle.table.price.label"
              ),
              selector: "price",
              grow: 0.4,
              cell: row => (
                <Text bold data-tag="allowRowEvents">
                  {row.price.amount} {row.price.currency}
                </Text>
              )
            },
            {
              name: t(
                "programBuilder.program.details.bundle.table.budget.label"
              ),
              selector: "budget",
              grow: 0.4,
              cell: row => (
                <Text bold data-tag="allowRowEvents">
                  {row.totalBudget} {row.price.currency}
                </Text>
              ),
              omit: !canSeeBudget
            },
            {
              name: t(
                "programBuilder.program.details.bundle.table.paidBy.label"
              ),
              selector: "paidBy",
              cell: row => (
                <Text bold data-tag="allowRowEvents">
                  {row.paidBy}
                </Text>
              )
            },
            {
              name: t(
                "programBuilder.program.details.bundle.table.campaigns.label"
              ),
              cell: (row: ProgramService.BundleModel) => {
                const numberOfCampaigns =
                  (row.metaTemplates?.length ?? 0) +
                  (row.deltaTemplates?.length ?? 0) +
                  (row.customTemplates?.length ?? 0);

                if (numberOfCampaigns === 0) {
                  return <Text data-tag="allowRowEvents">-</Text>;
                }

                return (
                  <Popover
                    trigger={
                      <Text data-tag="allowRowEvents">{numberOfCampaigns}</Text>
                    }
                  >
                    <ul>
                      {row.metaTemplates &&
                        row.metaTemplates.map(x => (
                          <li key={x.id}>{x.name}</li>
                        ))}
                      {row.deltaTemplates &&
                        row.deltaTemplates.map(x => (
                          <li key={x.id}>{x.name}</li>
                        ))}
                      {row.customTemplates &&
                        row.customTemplates.map(x => (
                          <li key={x.id}>{x.name}</li>
                        ))}
                    </ul>
                  </Popover>
                );
              }
            },
            {
              name: t(
                "programBuilder.program.details.bundle.table.default.label"
              ),
              selector: "default",
              right: true,
              width: "48px",
              cell: row => <BooleanCell checked={row.default ?? false} />
            },
            {
              name: "",
              button: true,
              right: true,
              width: "42px",
              cell: row => (
                <Button
                  logLabel="copy-bundle-button"
                  variant="text"
                  onClick={() => {
                    createSnapshot();
                    append({
                      ...row,
                      id: undefined,
                      name: `${row.name} (Copy)`,
                      default: false
                    });
                    onAction(fields.length);
                    setIsNewBundle(true);
                  }}
                  disabled={disabledFields("bundleGroups.bundles")}
                >
                  <i className="uil uil-copy is-size-4" />
                </Button>
              )
            },

            {
              name: "",
              button: true,
              right: true,
              width: "42px",
              cell: (_, index) => (
                <Button
                  logLabel="edit-bundle-button"
                  data-testid={`edit-bundle-${bundleGroupIndex}-${index}`}
                  variant="text"
                  onClick={() => {
                    createSnapshot();
                    onAction(index);
                  }}
                >
                  <i className="uil uil-pen is-size-4" />
                </Button>
              )
            },
            {
              name: "",
              button: true,
              right: true,
              width: "42px",
              cell: (row, index) => (
                <Button
                  logLabel="delete-bundle-button"
                  data-testid={`delete-bundle-button`}
                  variant="text"
                  onClick={() => {
                    const shouldRemove = window.confirm(
                      `${t(
                        "programBuilder.program.details.bundle.table.deleteIcon.confirmatioMessage"
                      )} ${row.name}?`
                    );
                    if (shouldRemove) {
                      remove(index);
                    }
                  }}
                  disabled={disabledFields("bundleGroups.bundles")}
                >
                  <i className="uil uil-trash-alt is-size-4" />
                </Button>
              )
            }
          ]}
          highlightOnHover
          keyField="regex"
          data={bundleGroup.bundles ?? []}
        />

        <Button
          logLabel="add-bundle-button"
          variant="text"
          onClick={() => {
            const newBundle: Partial<ProgramService.BundleModel> = {
              name: "",
              maxNumberOfOrders: 0,
              estimatedReach: 0,
              paidBy: "Hq",
              paymentMethod: "Invoice",
              default: bundleGroup?.bundles?.length === 0 ?? false,
              internal: false,
              price: {
                amount: 0,
                currency: (hq?.currency ?? "SEK") as ProgramService.Currencies
              },
              emails: [],
              tickets: []
            };
            createSnapshot();
            append(newBundle);
            onAction(fields.length);
            setIsNewBundle(true);
          }}
          disabled={disabledFields("bundleGroups.bundles")}
        >
          {t(
            "programBuilder.program.details.bundleGroup.overview.button.addBundle"
          )}
        </Button>
      </Box>
    </>
  );
};
